'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import WaveBackground from './WaveBackground';

export default function HeroSection() {
  const badgeRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void };
    const init = async () => {
      const { gsap } = await import('gsap');
      ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
        tl.fromTo(badgeRef.current, { opacity: 0, y: 20, filter: 'blur(8px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8 })
          .fromTo(headlineRef.current, { opacity: 0, y: 40, filter: 'blur(12px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.0 }, '-=0.4')
          .fromTo(subRef.current, { opacity: 0, y: 24, filter: 'blur(8px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8 }, '-=0.5')
          .fromTo(ctaRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4');
      });
    };
    init();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section id="beranda" className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '80px 80px' }}>
      
      {/* Background layers */}
      <div className="absolute inset-0 z-0">
        <WaveBackground />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(163,230,53,0.06) 0%, transparent 70%)' }} />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(132,204,22,0.04) 0%, transparent 70%)' }} />
      </div>

      {/* Vertical beams */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute left-[15%] top-0 w-px h-full bg-white/[0.03] overflow-hidden"><div className="home-beam" /></div>
        <div className="absolute left-1/2 -translate-x-1/2 top-0 w-px h-full bg-white/[0.02] overflow-hidden"><div className="home-beam home-beam-delay-1" /></div>
        <div className="absolute right-[15%] top-0 w-px h-full bg-white/[0.03] overflow-hidden"><div className="home-beam home-beam-delay-2" /></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-20 md:pt-36 md:pb-28 w-full">
        <div className="max-w-4xl mx-auto text-center">
          <div ref={badgeRef} className="opacity-0 inline-flex items-center gap-2 mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-lime-400/30 bg-lime-400/10 text-lime-400 text-[11px] font-semibold tracking-[0.15em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse" />
              Kreatif Studio · AI · Automation
            </span>
          </div>

          <div ref={headlineRef} className="opacity-0 mb-6">
            <h1 className="font-light tracking-[-0.025em] text-white leading-[1.0]" style={{ fontSize: 'clamp(2.8rem, 8vw, 6rem)' }}>
              Kreatif{' '}
              <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16, #65a30d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Studio</span>
              <br />
              AI &{' '}
              <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16, #65a30d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Automation</span>
            </h1>
          </div>

          <p ref={subRef} className="opacity-0 text-zinc-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
            Komunitas para creator digital yang berfokus pada AI dan Automation. Kami menciptakan solusi digital inovatif — dari creative HUB, website development, chatbot cerdas, hingga sistem otomasi digital.
          </p>

          <div ref={ctaRef} className="opacity-0 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <a href="#kontak" className="px-8 py-4 text-sm font-semibold bg-white text-zinc-900 hover:bg-[#a3e635] hover:text-white transition-all duration-150 inline-flex items-center gap-2 w-full sm:w-auto justify-center">
              Hubungi Kami <ArrowRight size={16} />
            </a>
            <Link href="/login" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2">
              Masuk Dashboard <ArrowRight size={14} />
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 md:mt-20 grid grid-cols-3 gap-4 md:gap-8 max-w-lg mx-auto">
            {[{ value: '50+', label: 'Proyek Selesai' }, { value: '10×', label: 'Lebih Cepat' }, { value: '100%', label: 'Klien Puas' }].map(s => (
              <div key={s.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-bold text-white tracking-tight">{s.value}</span>
                <span className="text-[11px] text-zinc-500 tracking-wide text-center">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#09090b] to-transparent pointer-events-none z-10" />
    </section>
  );
}
